import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TableContainer,
  Box,
  Typography,
  Grid,
  Paper,
  CircularProgress,
  Alert,
} from "@mui/material";
import { Link, useNavigate, useParams } from "react-router-dom";
import BookingOverview from "./BookingOverview";
import GuestDetails from "./GuestDetails";
import PaymentDetails from "./PaymentDetails";
import {
  createCustomerTenancy,
  getCustomersByTenancyId,
  getTenancyById,
  updateTenancyById,
  checkoutTenancy,
  renewTenancy,
  changeRoomTenancy,
} from "../TenancyApi";
import { useEffect, useState, useContext } from "react";
import StayDetails from "./StayDetails";
import CheckoutDialog from "./CheckoutForm";
import RenewForm from "./RenewForm";
import Header from "../Header";
import ChangeRoomForm from "./ChangeRoomForm";
import CommonComponents from "../CommonComponents";
import { format } from 'date-fns';
import { formatLocalDate } from "../CommonFunctions";
import AddReview from "../AddReview";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
} from "@mui/material";

export default function BookingDetails() {
  const { tenancyId } = useParams();
  const [tenancyDetails, setTenancyDetails] = useState({});
  const [customersDetails, setCustomerDetails] = useState([]);
  const [showCheckoutForm, setShowCheckoutForm] = useState(false);
  const [showRenewForm, setShowRenewForm] = useState(false);
  const [showChangeRoomForm, setShowChangeRoomForm] = useState(false);
  const [description, setDescription] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // ---- Post-action review prompt state ----
  // reviewPrompt: { open, action: "checkout" | "renew" }
  const [reviewPrompt, setReviewPrompt] = useState({ open: false, action: null });
  const [showAddReview, setShowAddReview] = useState(false);
  const [pendingNavigation, setPendingNavigation] = useState(null);

  const navigate = useNavigate();
  const { displayNotificationMessage } = useContext(CommonComponents);

  // ---------------- FETCHING DATA --------------------
  useEffect(() => {
    if (tenancyId) {
      getApiTenancyById(tenancyId);
      getApiCustomersByTenancyId(tenancyId);
    }
  }, [tenancyId]);

  const getApiCustomersByTenancyId = async (tenancyId) => {
    try {
      const response = await getCustomersByTenancyId(tenancyId);
      if (response) {
        setCustomerDetails(response);
      }
    } catch (error) {
      console.log(error);
    }
  };

  const getApiTenancyById = async (tenancyId) => {
    try {
      const response = await getTenancyById(tenancyId);
      if (response) {
        setTenancyDetails(response);
      }
    } catch (error) {
      console.log(error);
    }
  };

  const updateApiTenancyById = async (tenancyId, body) => {
    try {
      const response = await updateTenancyById(tenancyId, body);
      if (response) {
        setTenancyDetails(response);
        if (response.tenancyDescription) {
          setDescription(response.tenancyDescription);
        }
        return true;
      }
    } catch (error) {
      console.log(error);
      return false;
    }
  };

  // Sync local description when tenancyDetails refreshes
  useEffect(() => {
    if (tenancyDetails) {
      setDescription(tenancyDetails.tenancyDescription || "");
    }
  }, [tenancyDetails]);

  // ---------------------- SAVE DESCRIPTION -----------------------
  const handleSaveDescription = async () => {
    try {
      const success = await updateApiTenancyById(tenancyId, { tenancyDescription: description });
      if (success) {
        displayNotificationMessage("success", "Notes saved successfully!");
      } else {
        displayNotificationMessage("error", "Failed to save notes. Please try again.");
      }
    } catch (error) {
      displayNotificationMessage("error", "Failed to save notes. Please try again.");
    }
  };

  // ---------------------- CHECKOUT -----------------------
  const handleCheckout = async (actualCheckoutDate) => {
    if (loading) return; // Prevent double-click

    try {
      setLoading(true);
      setError(null);

      // Call V2 API
      await checkoutTenancy(tenancyId, actualCheckoutDate);

      // Refresh data
      await getApiTenancyById(tenancyId);

      setShowCheckoutForm(false);

      // Success notification
      const formattedDate = format(formatLocalDate(actualCheckoutDate), 'MMM dd, yyyy');
      displayNotificationMessage("success", `Guest checked out successfully on ${formattedDate}`);

      // Prompt manager to (optionally) leave a review for the guest(s)
      setReviewPrompt({ open: true, action: "checkout" });

    } catch (error) {
      console.error("Checkout failed:", error);
      const errorMsg = error.response?.data?.error || "Checkout failed. Please try again.";
      setError(errorMsg);
      displayNotificationMessage("error", errorMsg);
    } finally {
      setLoading(false);
    }
  };

  // ---------------------- RENEW -----------------------
  const handleRenew = async (renewData) => {
    if (loading) return; // Prevent double-click

    try {
      setLoading(true);
      setError(null);

      // Call V2 API
      const response = await renewTenancy(tenancyId, {
        checkin: renewData.fromDate,
        checkout: renewData.toDate,
        rate: renewData.rate,
        rentalType: renewData.rentalType,
      });

      if (response && response.data) {
        setShowRenewForm(false);

        // Success notification
        const fromDate = format(formatLocalDate(renewData.fromDate), 'MMM dd, yyyy');
        const toDate = format(formatLocalDate(renewData.toDate), 'MMM dd, yyyy');
        displayNotificationMessage("success", `Tenancy renewed from ${fromDate} to ${toDate}`);

        // Hold off navigating until the manager has had a chance to
        // add (or skip) a review, then follow through to the new tenancy.
        setPendingNavigation(`/booking/${response.data}`);
        setReviewPrompt({ open: true, action: "renew" });
      }

    } catch (error) {
      console.error("Renew failed:", error);
      const errorMsg = error.response?.data?.error || "Renewal failed. Please try again.";
      setError(errorMsg);
      displayNotificationMessage("error", errorMsg);
    } finally {
      setLoading(false);
    }
  };

  // ---------------------- CHANGE ROOM -----------------------
  const handleChangeRoom = async (newRoomId) => {
    if (loading) return; // Prevent double-click

    try {
      setLoading(true);
      setError(null);

      // Call V2 API
      const response = await changeRoomTenancy(tenancyId, newRoomId);

      if (response && response.data) {
        setShowChangeRoomForm(false);

        // Success notification
        displayNotificationMessage("success", `Room changed successfully to Room ${newRoomId}`);

        // Navigate to new tenancy
        navigate(`/booking/${response.data}`);
      }

    } catch (error) {
      console.error("Change room failed:", error);
      const errorMsg = error.response?.data?.error || "Room change failed. Please try again.";
      setError(errorMsg);
      displayNotificationMessage("error", errorMsg);
    } finally {
      setLoading(false);
    }
  };

  // ---------------------- POST-ACTION REVIEW PROMPT -----------------------
  // Manager chose to leave a review — open the AddReview dialog.
  const handleOpenReviewFromPrompt = () => {
    setReviewPrompt({ open: false, action: null });
    setShowAddReview(true);
  };

  // AddReview dialog closed (saved or cancelled) — follow through on any
  // navigation that was waiting on this (e.g. renew -> new tenancy page).
  const handleAddReviewClose = (open) => {
    setShowAddReview(open);
    if (!open && pendingNavigation) {
      navigate(pendingNavigation);
      setPendingNavigation(null);
    }
  };

  return (
    <>
      <Header />

      {/* Error Alert */}
      {error && (
        <Box px={4} pt={2}>
          <Alert severity="error" onClose={() => setError(null)}>
            {error}
          </Alert>
        </Box>
      )}

      {/* Loading Overlay */}
      {loading && (
        <Box
          sx={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            bgcolor: "rgba(0,0,0,0.3)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 9999,
          }}
        >
          <CircularProgress />
        </Box>
      )}

      {/* TOP BOOKING OVERVIEW TABLE */}
      <BookingOverview
        tenancyId={tenancyId}
        customersDetails={customersDetails}
        tenancyDetails={tenancyDetails}
      />

      {/* MAIN GRID */}
      <Grid container sx={{ p: 2 }} spacing={2}>
        {/* LEFT SIDE (3/4) */}
        <Grid item xs={12} md={9}>
          <GuestDetails tenancyId={tenancyId} customersDetails={customersDetails} />
          <br />
          <StayDetails
            tenancyId={tenancyId}
            handleTenancyUpdate={updateApiTenancyById}
            tenancyDetails={tenancyDetails}
          />
          <br />
          <PaymentDetails
            tenancyId={tenancyId}
            tenancyDetails={tenancyDetails}
            customersDetails={customersDetails}
          />
        </Grid>

        {/* RIGHT SIDE */}
        <Grid item xs={12} md={3}>
          <Paper
            elevation={3}
            sx={{
              bgcolor: "white",
              px: 2,
              height: "100%",
              borderRadius: 1,
              display: "flex",
              flexDirection: "column",
              gap: 2,
            }}
          >
            {/* DESCRIPTION */}
            <Box>
              <Typography sx={{ fontWeight: 700, fontSize: "0.95rem" }}>
                Notes / Description
              </Typography>
              <textarea
                style={{
                  width: "100%",
                  height: "120px",
                  resize: "none",
                  fontSize: "0.9rem",
                  borderRadius: "6px",
                  border: "1px solid #ddd",
                  outline: "none",
                }}
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                disabled={loading}
              />
            </Box>

            {/* ACTION BUTTONS */}
            {tenancyDetails.status === "active" && (
              <Grid container spacing={1}>
                <Grid item xs={6}>
                  <Paper
                    elevation={1}
                    sx={{
                      textAlign: "center",
                      py: 1,
                      fontWeight: 600,
                      bgcolor: loading ? "#ccc" : "#eef6ff",
                      color: loading ? "#666" : "#0066ff",
                      cursor: loading ? "not-allowed" : "pointer",
                      "&:hover": { bgcolor: loading ? "#ccc" : "#e2efff" },
                    }}
                    onClick={loading ? null : handleSaveDescription}
                  >
                    Save
                  </Paper>
                </Grid>

                <Grid item xs={6}>
                  <Paper
                    elevation={1}
                    sx={{
                      textAlign: "center",
                      py: 1,
                      fontWeight: 600,
                      bgcolor: loading ? "#ccc" : "#eef6ff",
                      color: loading ? "#666" : "#0066ff",
                      cursor: loading ? "not-allowed" : "pointer",
                      "&:hover": { bgcolor: loading ? "#ccc" : "#e2efff" },
                    }}
                    onClick={loading ? null : () => setShowCheckoutForm(true)}
                  >
                    Checkout
                  </Paper>
                </Grid>

                <Grid item xs={6}>
                  <Paper
                    elevation={1}
                    sx={{
                      textAlign: "center",
                      py: 1,
                      fontWeight: 600,
                      bgcolor: loading ? "#ccc" : "#eef6ff",
                      color: loading ? "#666" : "#0066ff",
                      cursor: loading ? "not-allowed" : "pointer",
                      "&:hover": { bgcolor: loading ? "#ccc" : "#e2efff" },
                    }}
                    onClick={loading ? null : () => setShowRenewForm(true)}
                  >
                    Renew
                  </Paper>
                </Grid>

                <Grid item xs={6}>
                  <Paper
                    elevation={1}
                    sx={{
                      textAlign: "center",
                      py: 1,
                      fontWeight: 600,
                      bgcolor: loading ? "#ccc" : "#eef6ff",
                      color: loading ? "#666" : "#0066ff",
                      cursor: loading ? "not-allowed" : "pointer",
                      "&:hover": { bgcolor: loading ? "#ccc" : "#e2efff" },
                    }}
                    onClick={loading ? null : () => setShowChangeRoomForm(true)}
                  >
                    Change Room
                  </Paper>
                </Grid>
              </Grid>
            )}
          </Paper>
        </Grid>
      </Grid>

      {/* MODALS */}
      {showCheckoutForm && (
        <CheckoutDialog
          open={showCheckoutForm}
          onClose={() => setShowCheckoutForm(false)}
          tenancyDetails={tenancyDetails}
          handleCheckout={handleCheckout}
          loading={loading}
        />
      )}

      {showRenewForm && (
        <RenewForm
          open={showRenewForm}
          onClose={() => setShowRenewForm(false)}
          tenancyDetails={tenancyDetails}
          handleRenew={handleRenew}
          loading={loading}
        />
      )}

      {showChangeRoomForm && (
        <ChangeRoomForm
          open={showChangeRoomForm}
          onClose={() => setShowChangeRoomForm(false)}
          tenancyDetails={tenancyDetails}
          handleChangeRoom={handleChangeRoom}
          loading={loading}
        />
      )}

      {/* POST-CHECKOUT / POST-RENEW REVIEW PROMPT */}
      {/* Review is mandatory: no Skip button, and backdrop/Escape close is blocked. */}
      <Dialog
        open={reviewPrompt.open}
        onClose={(event, reason) => {
          if (reason === "backdropClick" || reason === "escapeKeyDown") return;
        }}
        disableEscapeKeyDown
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle sx={{ fontWeight: 700 }}>Review Required</DialogTitle>
        <DialogContent>
          <span color="text.secondary">
            {reviewPrompt.action === "renew"
              ? "Tenancy renewed. Please add a review for this guest to continue."
              : "Guest checked out. Please add a review for this guest to continue."}
          </span>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleOpenReviewFromPrompt} variant="contained">
            Add Review
          </Button>
        </DialogActions>
      </Dialog>

      {/* ADD REVIEW */}
      {showAddReview && (
        <AddReview
          showAddReview={showAddReview}
          setShowAddReview={handleAddReviewClose}
          tenancyId={tenancyId}
          customersDetails={customersDetails}
          required
        />
      )}
    </>
  );
}